import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
	ArrowDownRight,
	ArrowUpRight,
	Banknote,
	Briefcase,
	CalendarClock,
	Eye,
	Globe2,
	Newspaper,
	Radar,
} from 'lucide-react';
import { useState } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/shadcn/card.tsx';
import { ScrollArea } from '~/components/shadcn/scroll-area.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/shadcn/select.tsx';
import { Skeleton } from '~/components/shadcn/skeleton.tsx';
import type { WatchlistForLayoutType } from '~/database/supabase/watchlist/types.ts';
import { useTranslations } from '~/i18n/use-translations.ts';
import type { InstrumentDisplayDataResponse } from '~/integrations/yahoo/queries.js';
import { requireAuthenticatedBeforeLoad } from '~/routing/require-authenticated-before-load.ts';
import { getAppShellDataQueryOptions } from '~/server/app/app-shell-data-query.ts';
import { useStore } from '~/store/index.ts';
import { cn } from '~/utils/cn.ts';

import { getDashboardPageDataQueryOptions } from '../server/dashboard/dashboard-page-data-query.ts';
import type { DashboardHeadline, DashboardMarketItem } from '../server/dashboard/get-dashboard-page-data.ts';

type DisplayDataMap =
	| Map<string, InstrumentDisplayDataResponse>
	| Record<string, InstrumentDisplayDataResponse>
	| null
	| undefined;

type WatchlistPulseItem = {
	readonly changePercent: number | null;
	readonly price: number | null;
	readonly shortName: string;
	readonly ticker: string;
};

interface HeadlineLanguageOption {
	readonly label: string;
	readonly value: string;
}

function getDisplayDataEntries(instrumentFetches: DisplayDataMap): Array<[string, InstrumentDisplayDataResponse]> {
	if (!instrumentFetches) {
		return [];
	}

	if (instrumentFetches instanceof Map) {
		return Array.from(instrumentFetches.entries());
	}

	return Object.entries(instrumentFetches);
}

function getWatchlistPulse(
	watchlists: WatchlistForLayoutType[] | null | undefined,
	instrumentFetches: DisplayDataMap,
): WatchlistPulseItem[] {
	const watchlistTickers = new Set<string>();
	for (const watchlist of watchlists ?? []) {
		for (const junction of watchlist.watch_list_instrument_junction ?? []) {
			if (junction.instrument?.ticker) {
				watchlistTickers.add(junction.instrument.ticker);
			}
		}
	}

	return getDisplayDataEntries(instrumentFetches)
		.filter(([ticker]) => watchlistTickers.has(ticker))
		.map(([, value]) => ({
			changePercent: value.regularMarketChangePercent ?? null,
			price: value.regularMarketPrice ?? null,
			shortName: value.shortName ?? value.symbol,
			ticker: value.symbol,
		}))
		.sort((left, right) => Math.abs(right.changePercent ?? 0) - Math.abs(left.changePercent ?? 0))
		.slice(0, 6);
}

function getTrackedIndexPulse(
	watchlists: WatchlistForLayoutType[] | null | undefined,
	instrumentFetches: DisplayDataMap,
	label: string,
): DashboardMarketItem[] {
	const watchlistTickers = new Set<string>();
	for (const watchlist of watchlists ?? []) {
		for (const junction of watchlist.watch_list_instrument_junction ?? []) {
			if (junction.instrument?.ticker) {
				watchlistTickers.add(junction.instrument.ticker);
			}
		}
	}

	return getDisplayDataEntries(instrumentFetches)
		.filter(([ticker, value]) => watchlistTickers.has(ticker) && value.quoteType === 'INDEX')
		.map(([, value]) => ({
			changePercent: value.regularMarketChangePercent ?? null,
			label,
			price: value.regularMarketPrice ?? null,
			shortName: value.shortName ?? value.displayName ?? value.symbol,
			ticker: value.symbol,
		}))
		.sort((left, right) => Math.abs(right.changePercent ?? 0) - Math.abs(left.changePercent ?? 0))
		.slice(0, 4);
}

function formatCurrency(value: number | null, currency: string, userLocale: string): string {
	if (value === null) {
		return '—';
	}

	return value.toLocaleString(userLocale, {
		currency,
		maximumFractionDigits: Math.abs(value) > 10_000 ? 0 : 2,
		minimumFractionDigits: Math.abs(value) > 10_000 ? 0 : 2,
		style: 'currency',
	});
}

function formatPercent(value: number | null, userLocale: string): string {
	if (value === null) {
		return '—';
	}

	return `${value.toLocaleString(userLocale, {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	})}%`;
}

function formatPrice(value: number | null, userLocale: string): string {
	if (value === null) {
		return '—';
	}

	return value.toLocaleString(userLocale, {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2,
	});
}

function getChangeTextClassName(value: number | null, neutralClassName = 'text-foreground'): string {
	if (value === null) {
		return neutralClassName;
	}

	if (value > 0) {
		return 'text-positive';
	}

	if (value < 0) {
		return 'text-negative';
	}

	return neutralClassName;
}

function formatPublishedAt(value: number, userLocale: string): string {
	return new Date(value).toLocaleString(userLocale, {
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		month: 'short',
	});
}

function formatEventDate(value: string | null, userLocale: string, unavailableLabel: string): string {
	if (!value) {
		return unavailableLabel;
	}

	return new Date(value).toLocaleDateString(userLocale, {
		day: 'numeric',
		month: 'short',
		weekday: 'short',
	});
}

function getHeadlineLanguageLabel(language: string): string {
	switch (language) {
		case 'da':
			return 'Dansk';
		case 'en':
			return 'English';
		case 'fi':
			return 'Suomi';
		case 'is':
			return 'Islenska';
		case 'no':
			return 'Norsk';
		case 'sv':
			return 'Svenska';
		default:
			return language.toUpperCase();
	}
}

function getHeadlineLanguageOptions(
	headlines: DashboardHeadline[],
	allLanguagesLabel: string,
): HeadlineLanguageOption[] {
	const languages = Array.from(new Set(headlines.map((headline) => headline.lang)));

	return [
		{ label: allLanguagesLabel, value: 'all' },
		...languages.map((language) => ({
			label: getHeadlineLanguageLabel(language),
			value: language,
		})),
	];
}

function getDefaultHeadlineLanguage(headlines: DashboardHeadline[], userLocale: string): string {
	const availableLanguages = new Set(headlines.map((headline) => headline.lang));
	const preferredLanguage = userLocale.split('-')[0]?.toLowerCase();

	if (preferredLanguage && availableLanguages.has(preferredLanguage)) {
		return preferredLanguage;
	}

	return 'all';
}

export const Route = createFileRoute('/dashboard')({
	beforeLoad: requireAuthenticatedBeforeLoad,
	component: DashboardPage,
	loader: ({ context }) => context.queryClient.ensureQueryData(getDashboardPageDataQueryOptions()),
	pendingComponent: DashboardPagePending,
	pendingMinMs: 250,
});

function DashboardPagePending() {
	return (
		<div className="h-full overflow-auto px-4 py-4 sm:px-6">
			<div className="mx-auto flex w-full max-w-[1480px] flex-col gap-4">
				<Skeleton className="h-40 w-full rounded-none border border-border/70 bg-card/95" />
				<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
					<Skeleton className="h-72 w-full rounded-none border border-border/70 bg-card/95" />
					<Skeleton className="h-72 w-full rounded-none border border-border/70 bg-card/95" />
				</div>
				<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
					<Skeleton className="h-52 w-full rounded-none border border-border/70 bg-card/95" />
					<Skeleton className="h-52 w-full rounded-none border border-border/70 bg-card/95" />
					<Skeleton className="h-52 w-full rounded-none border border-border/70 bg-card/95" />
				</div>
			</div>
		</div>
	);
}

function DashboardPage() {
	const t = useTranslations();
	const { data: loaderData } = useSuspenseQuery(getDashboardPageDataQueryOptions());
	const { data: rootLoaderData } = useSuspenseQuery(getAppShellDataQueryOptions());
	const instrumentFetches = useStore((state) => state.instrumentFetches);
	const watchlistPulse = getWatchlistPulse(rootLoaderData.watchlists ?? null, instrumentFetches);
	const trackedIndexPulse = getTrackedIndexPulse(
		rootLoaderData.watchlists ?? null,
		instrumentFetches,
		t('dashboard.trackedIndex'),
	);
	const headlineLanguageOptions = getHeadlineLanguageOptions(loaderData.headlines, t('dashboard.allLanguages'));
	const defaultHeadlineLanguage = getDefaultHeadlineLanguage(loaderData.headlines, loaderData.userLocale);
	const [selectedHeadlineLanguage, setSelectedHeadlineLanguage] = useState(defaultHeadlineLanguage);
	const resolvedHeadlineLanguage = headlineLanguageOptions.some((option) => option.value === selectedHeadlineLanguage)
		? selectedHeadlineLanguage
		: defaultHeadlineLanguage;
	const filteredHeadlines =
		resolvedHeadlineLanguage === 'all'
			? loaderData.headlines
			: loaderData.headlines.filter((headline) => headline.lang === resolvedHeadlineLanguage);

	const strongestPortfolio = loaderData.portfolioSummaries
		.slice()
		.sort(
			(left, right) =>
				(right.weeklyPercent ?? Number.NEGATIVE_INFINITY) - (left.weeklyPercent ?? Number.NEGATIVE_INFINITY),
		)[0];

	const weakestPortfolio = loaderData.portfolioSummaries
		.slice()
		.sort(
			(left, right) =>
				(left.weeklyPercent ?? Number.POSITIVE_INFINITY) - (right.weeklyPercent ?? Number.POSITIVE_INFINITY),
		)[0];

	const watchlistInstrumentCount = (rootLoaderData.watchlists ?? []).reduce((sum, watchlist) => {
		return sum + (watchlist.watch_list_instrument_junction?.length ?? 0);
	}, 0);

	return (
		<ScrollArea
			classNames={{
				root: 'h-full w-full',
				viewport: 'h-full w-full [&>div]:block!',
			}}
		>
			<div className="px-4 py-4 sm:px-6">
				<div className="mx-auto flex w-full max-w-[1480px] flex-col gap-4">
					<section className="border border-border/70 bg-card/95 px-5 py-5 shadow-sm">
						<div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
							<div className="space-y-2">
								<p className="font-mono text-[11px] text-primary/85 uppercase tracking-[0.24em]">
									{t('dashboard.weeklyBrief')}
								</p>
								<h1 className="font-mono text-3xl text-foreground tracking-tight">{t('nav.dashboard')}</h1>
								<p className="max-w-3xl text-muted-foreground text-sm leading-6">{t('dashboard.intro')}</p>
							</div>
							<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
								<div className="border border-border/70 bg-muted/10 px-3 py-2">
									<p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em]">{t('nav.portfolios')}</p>
									<p className="mt-1 font-mono text-foreground text-lg">{loaderData.portfolioSummaries.length}</p>
								</div>
								<div className="border border-border/70 bg-muted/10 px-3 py-2">
									<p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em]">
										{t('dashboard.watchlistItems')}
									</p>
									<p className="mt-1 font-mono text-foreground text-lg">{watchlistInstrumentCount}</p>
								</div>
								<div className="border border-border/70 bg-muted/10 px-3 py-2">
									<p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em]">
										{t('dashboard.newsThisWeek')}
									</p>
									<p className="mt-1 font-mono text-foreground text-lg">{loaderData.headlines.length}</p>
								</div>
								<div className="border border-border/70 bg-muted/10 px-3 py-2">
									<p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em]">
										{t('dashboard.nextWeekEvents')}
									</p>
									<p className="mt-1 font-mono text-foreground text-lg">{loaderData.calendar.length}</p>
								</div>
							</div>
						</div>
					</section>

					<section className="grid grid-cols-1 gap-4 xl:grid-cols-12">
						<Card className="xl:col-span-7">
							<CardHeader className="pb-3">
								<div className="flex items-center gap-2">
									<Briefcase className="size-4 text-primary/85" />
									<CardTitle className="text-lg">{t('dashboard.portfolioWeek')}</CardTitle>
								</div>
								<CardDescription>{t('dashboard.portfolioWeekDescription')}</CardDescription>
							</CardHeader>
							<CardContent className="grid gap-3">
								{loaderData.portfolioSummaries.length === 0 ? (
									<p className="text-muted-foreground text-sm">{t('dashboard.noPortfolios')}</p>
								) : (
									loaderData.portfolioSummaries.map((portfolio) => (
										<div
											className="grid grid-cols-1 gap-3 border border-border/70 bg-muted/10 px-4 py-3 md:grid-cols-[minmax(0,1fr)_auto_auto]"
											key={portfolio.id}
										>
											<div className="min-w-0">
												<p className="font-medium text-foreground text-sm">{portfolio.name}</p>
												<p className="text-[11px] text-muted-foreground uppercase tracking-[0.16em]">
													{t('dashboard.activeHoldings', { count: portfolio.holdingsCount })}
												</p>
											</div>
											<div>
												<p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em]">
													{t('dashboard.currentValue')}
												</p>
												<p className="font-mono text-foreground text-sm">
													{formatCurrency(portfolio.currentValue, portfolio.currency, loaderData.userLocale)}
												</p>
											</div>
											<div>
												<p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em]">7d</p>
												<p className={cn('font-mono text-sm', getChangeTextClassName(portfolio.weeklyPercent))}>
													{formatPercent(portfolio.weeklyPercent, loaderData.userLocale)}
												</p>
											</div>
										</div>
									))
								)}
							</CardContent>
						</Card>

						<Card className="xl:col-span-5">
							<CardHeader className="pb-3">
								<div className="flex items-center gap-2">
									<Radar className="size-4 text-primary/85" />
									<CardTitle className="text-lg">{t('dashboard.atGlance')}</CardTitle>
								</div>
								<CardDescription>{t('dashboard.atGlanceDescription')}</CardDescription>
							</CardHeader>
							<CardContent className="space-y-3">
								<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
									<div className="border border-border/70 bg-muted/10 px-4 py-3">
										<p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em]">
											{t('dashboard.strongestPortfolio')}
										</p>
										<p className="mt-2 font-medium text-foreground text-sm">{strongestPortfolio?.name ?? '—'}</p>
										<p className="mt-1 font-mono text-positive text-sm">
											{strongestPortfolio
												? formatPercent(strongestPortfolio.weeklyPercent, loaderData.userLocale)
												: '—'}
										</p>
									</div>
									<div className="border border-border/70 bg-muted/10 px-4 py-3">
										<p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em]">
											{t('dashboard.weakestPortfolio')}
										</p>
										<p className="mt-2 font-medium text-foreground text-sm">{weakestPortfolio?.name ?? '—'}</p>
										<p className="mt-1 font-mono text-negative text-sm">
											{weakestPortfolio ? formatPercent(weakestPortfolio.weeklyPercent, loaderData.userLocale) : '—'}
										</p>
									</div>
								</div>

								<div className="space-y-2">
									<p className="text-[10px] text-muted-foreground uppercase tracking-[0.18em]">
										{t('dashboard.watchlistPulse')}
									</p>
									{watchlistPulse.length === 0 ? (
										<p className="text-muted-foreground text-sm">{t('dashboard.noWatchlistInstruments')}</p>
									) : (
										watchlistPulse.map((item) => (
											<div
												className="flex items-center justify-between gap-3 border border-border/70 bg-muted/10 px-4 py-2.5"
												key={item.ticker}
											>
												<div className="min-w-0">
													<p className="truncate font-medium text-foreground text-sm">{item.shortName}</p>
													<p className="font-mono text-[11px] text-muted-foreground uppercase tracking-[0.16em]">
														{item.ticker}
													</p>
												</div>
												<div className="text-right">
													<p className="font-mono text-foreground text-sm">
														{formatPrice(item.price, loaderData.userLocale)}
													</p>
													<p
														className={cn(
															'font-mono text-xs',
															getChangeTextClassName(item.changePercent, 'text-muted-foreground'),
														)}
													>
														{formatPercent(item.changePercent, loaderData.userLocale)}
													</p>
												</div>
											</div>
										))
									)}
								</div>
							</CardContent>
						</Card>
					</section>

					<section className="grid grid-cols-1 gap-4 xl:grid-cols-12">
						<Card className="xl:col-span-7">
							<CardHeader className="pb-3">
								<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
									<div>
										<div className="flex items-center gap-2">
											<Newspaper className="size-4 text-primary/85" />
											<CardTitle className="text-lg">{t('dashboard.thisWeeksNews')}</CardTitle>
										</div>
										<CardDescription>{t('dashboard.newsDescription')}</CardDescription>
									</div>
									{headlineLanguageOptions.length > 2 ? (
										<div className="w-full sm:w-[180px]">
											<p className="mb-1 text-[10px] text-muted-foreground uppercase tracking-[0.18em]">
												{t('dashboard.language')}
											</p>
											<Select onValueChange={setSelectedHeadlineLanguage} value={resolvedHeadlineLanguage}>
												<SelectTrigger className="w-full">
													<SelectValue placeholder={t('dashboard.allLanguages')} />
												</SelectTrigger>
												<SelectContent>
													{headlineLanguageOptions.map((option) => (
														<SelectItem key={option.value} value={option.value}>
															{option.label}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
									) : null}
								</div>
							</CardHeader>
							<CardContent className="space-y-3">
								{filteredHeadlines.length === 0 ? (
									<p className="text-muted-foreground text-sm">
										{resolvedHeadlineLanguage === 'all'
											? t('dashboard.noHeadlines')
											: t('dashboard.noHeadlinesForLanguage')}
									</p>
								) : (
									filteredHeadlines.map((headline) => (
										<div className="border border-border/70 bg-muted/10 px-4 py-3" key={headline.id}>
											<div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-[0.18em]">
												<span>{headline.companyName}</span>
												<span className="font-mono text-foreground">{headline.ticker}</span>
												<span>{formatPublishedAt(headline.publishedAt, loaderData.userLocale)}</span>
											</div>
											{headline.href ? (
												<a
													className="mt-2 inline-block text-foreground text-sm leading-6 hover:underline"
													href={headline.href}
													rel="noreferrer"
													target="_blank"
												>
													{headline.description}
												</a>
											) : (
												<p className="mt-2 text-foreground text-sm leading-6">{headline.description}</p>
											)}
											<div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
												{headline.dayChange ? (
													<span
														className={cn(
															headline.dayChange.startsWith('+') ? 'text-positive' : '',
															headline.dayChange.startsWith('-') ? 'text-negative' : 'text-muted-foreground',
														)}
													>
														{headline.dayChange}
													</span>
												) : null}
												{headline.dayVolume ? (
													<span className="text-muted-foreground">{headline.dayVolume}</span>
												) : null}
											</div>
										</div>
									))
								)}
								<a
									className="inline-flex items-center gap-1 text-primary text-xs hover:underline"
									href="/news?type=interest"
								>
									{t('dashboard.seeAllInterestNews')}
									<ArrowUpRight className="size-3.5" />
								</a>
							</CardContent>
						</Card>

						<Card className="xl:col-span-5">
							<CardHeader className="pb-3">
								<div className="flex items-center gap-2">
									<CalendarClock className="size-4 text-primary/85" />
									<CardTitle className="text-lg">{t('dashboard.nextWeek')}</CardTitle>
								</div>
								<CardDescription>{t('dashboard.nextWeekDescription')}</CardDescription>
							</CardHeader>
							<CardContent className="space-y-3">
								{loaderData.calendar.length === 0 ? (
									<p className="text-muted-foreground text-sm">{t('dashboard.noUpcomingEvents')}</p>
								) : (
									loaderData.calendar.map((event) => (
										<div className="border border-border/70 bg-muted/10 px-4 py-3" key={event.id}>
											<div className="flex items-center justify-between gap-3">
												<div className="min-w-0">
													<p className="truncate font-medium text-foreground text-sm">{event.companyName}</p>
													<p className="font-mono text-[11px] text-muted-foreground uppercase tracking-[0.16em]">
														{event.ticker}
													</p>
												</div>
												<p className="shrink-0 text-[11px] text-muted-foreground uppercase tracking-[0.16em]">
													{formatEventDate(event.date, loaderData.userLocale, t('dashboard.dateUnavailable'))}
												</p>
											</div>
											<p className="mt-2 text-foreground text-sm leading-6">{event.description}</p>
										</div>
									))
								)}
								<a className="inline-flex items-center gap-1 text-primary text-xs hover:underline" href="/calendar">
									{t('dashboard.openCalendar')}
									<ArrowDownRight className="size-3.5" />
								</a>
							</CardContent>
						</Card>
					</section>

					<section className="grid grid-cols-1 gap-4 xl:grid-cols-12">
						<Card className="xl:col-span-4">
							<CardHeader className="pb-3">
								<div className="flex items-center gap-2">
									<Globe2 className="size-4 text-primary/85" />
									<CardTitle className="text-lg">{t('dashboard.regionalMarkets')}</CardTitle>
								</div>
								<CardDescription>{t('dashboard.regionalMarketsDescription')}</CardDescription>
							</CardHeader>
							<CardContent className="space-y-3">
								{loaderData.countryBenchmarks.length === 0 ? (
									<p className="text-muted-foreground text-sm">{t('dashboard.noRegionalBenchmarks')}</p>
								) : (
									loaderData.countryBenchmarks.map((item) => (
										<Link
											className="flex items-center justify-between gap-3 border border-border/70 bg-muted/10 px-4 py-3 transition-colors hover:bg-muted/20"
											key={item.ticker}
											params={{ ticker: item.ticker }}
											to="/instrument/$ticker"
										>
											<div className="min-w-0">
												<p className="truncate font-medium text-foreground text-sm">{item.shortName}</p>
												<p className="text-[11px] text-muted-foreground uppercase tracking-[0.16em]">
													{item.label} · {item.ticker}
												</p>
											</div>
											<div className="text-right">
												<p className="font-mono text-foreground text-sm">
													{formatPrice(item.price, loaderData.userLocale)}
												</p>
												<p
													className={cn(
														'font-mono text-xs',
														getChangeTextClassName(item.changePercent, 'text-muted-foreground'),
													)}
												>
													{formatPercent(item.changePercent, loaderData.userLocale)}
												</p>
											</div>
										</Link>
									))
								)}
							</CardContent>
						</Card>

						<Card className="xl:col-span-4">
							<CardHeader className="pb-3">
								<div className="flex items-center gap-2">
									<Banknote className="size-4 text-primary/85" />
									<CardTitle className="text-lg">{t('dashboard.currencyContext')}</CardTitle>
								</div>
								<CardDescription>{t('dashboard.currencyContextDescription')}</CardDescription>
							</CardHeader>
							<CardContent className="space-y-3">
								{loaderData.currencyBenchmarks.length === 0 ? (
									<p className="text-muted-foreground text-sm">{t('dashboard.noCurrencyExposure')}</p>
								) : (
									loaderData.currencyBenchmarks.map((item) => (
										<Link
											className="flex items-center justify-between gap-3 border border-border/70 bg-muted/10 px-4 py-3 transition-colors hover:bg-muted/20"
											key={item.ticker}
											params={{ ticker: item.ticker }}
											to="/instrument/$ticker"
										>
											<div className="min-w-0">
												<p className="truncate font-medium text-foreground text-sm">{item.shortName}</p>
												<p className="text-[11px] text-muted-foreground uppercase tracking-[0.16em]">
													{item.label} · {item.ticker}
												</p>
											</div>
											<div className="text-right">
												<p className="font-mono text-foreground text-sm">
													{formatPrice(item.price, loaderData.userLocale)}
												</p>
												<p
													className={cn(
														'font-mono text-xs',
														getChangeTextClassName(item.changePercent, 'text-muted-foreground'),
													)}
												>
													{formatPercent(item.changePercent, loaderData.userLocale)}
												</p>
											</div>
										</Link>
									))
								)}
							</CardContent>
						</Card>

						<Card className="xl:col-span-4">
							<CardHeader className="pb-3">
								<div className="flex items-center gap-2">
									<Eye className="size-4 text-primary/85" />
									<CardTitle className="text-lg">{t('dashboard.trackedIndices')}</CardTitle>
								</div>
								<CardDescription>{t('dashboard.trackedIndicesDescription')}</CardDescription>
							</CardHeader>
							<CardContent className="space-y-3">
								{trackedIndexPulse.length === 0 ? (
									<div className="border border-border/70 border-dashed bg-muted/10 px-4 py-4">
										<p className="text-foreground text-sm">{t('dashboard.noWatchlistIndices')}</p>
										<p className="mt-1 text-muted-foreground text-sm">{t('dashboard.addIndexHint')}</p>
									</div>
								) : (
									trackedIndexPulse.map((item) => (
										<Link
											className="flex items-center justify-between gap-3 border border-border/70 bg-muted/10 px-4 py-3 transition-colors hover:bg-muted/20"
											key={item.ticker}
											params={{ ticker: item.ticker }}
											to="/instrument/$ticker"
										>
											<div className="min-w-0">
												<p className="truncate font-medium text-foreground text-sm">{item.shortName}</p>
												<p className="text-[11px] text-muted-foreground uppercase tracking-[0.16em]">{item.ticker}</p>
											</div>
											<div className="text-right">
												<p className="font-mono text-foreground text-sm">
													{formatPrice(item.price, loaderData.userLocale)}
												</p>
												<p
													className={cn(
														'font-mono text-xs',
														getChangeTextClassName(item.changePercent, 'text-muted-foreground'),
													)}
												>
													{formatPercent(item.changePercent, loaderData.userLocale)}
												</p>
											</div>
										</Link>
									))
								)}
							</CardContent>
						</Card>
					</section>
				</div>
			</div>
		</ScrollArea>
	);
}
